/**
 * POS · Recipes — define sellable drinks that deplete INGREDIENTS rather than
 * their own stock (add-pos-recipes-and-cups §3). A recipe = a sellable article
 * (its chain root) + ingredient rows ``(consumable, amount, unit)``. Selling it
 * fans out the ingredient depletion at settle; the recipe article holds no
 * stock of its own.
 *
 * Each row surfaces the DERIVED availability (servings buildable from the
 * limiting ingredient's whole units + open-container overlay) and the PROJECTED
 * per-serving COGS (a display projection — no Expense is booked; ingredients are
 * already cost-booked at procurement).
 *
 * FUNCTIONAL scaffold — plain AntD wired to the real ``/api/recipes`` endpoints.
 * The design pass restyles it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Empty,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  ApiError,
  api,
  type ArticleRow,
  type CreateRecipeRequest,
  type RecipeIngredientIn,
  type RecipeRow,
} from "../../api/client";
import {
  UNIT_LABEL,
  UNIT_OPTIONS,
  defaultUnitType,
  type RecipeUnitType as UnitType,
} from "../../lib/recipeUnits";
import { formatCents } from "../../utils/dateFormat";

const { Text } = Typography;

interface DraftIngredient {
  consumable_chain_root_id: number | null;
  amount: number | null;
  unit_type: UnitType;
}

const EMPTY_DRAFT: DraftIngredient = {
  consumable_chain_root_id: null,
  amount: null,
  unit_type: "liters",
};

export const PosRecipesPage = () => {
  const { message } = AntApp.useApp();

  const [recipes, setRecipes] = useState<RecipeRow[] | null>(null);
  const [articles, setArticles] = useState<ArticleRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Create/edit modal. ``editing`` null + open = create; non-null = edit
  // ingredients of an existing recipe (the article is fixed once created).
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RecipeRow | null>(null);
  const [articleChainRoot, setArticleChainRoot] = useState<number | null>(null);
  const [draftIngredients, setDraftIngredients] = useState<DraftIngredient[]>([
    { ...EMPTY_DRAFT },
  ]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [rs, arts] = await Promise.all([
        api.listRecipes(),
        api.listArticles({ active: true }),
      ]);
      setRecipes(rs);
      setArticles(arts);
    } catch (e) {
      setLoadError(
        e instanceof ApiError ? e.detail : "Failed to load recipes",
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // chain_root_id → display name. Recipes + ingredients key on chain roots, so
  // collapse the article list to one entry per chain (prefer the chain-root
  // article's own name).
  const nameByChainRoot = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of articles ?? []) {
      if (!m.has(a.chain_root_id) || a.is_chain_root)
        m.set(a.chain_root_id, a.name);
    }
    return m;
  }, [articles]);

  const articleByChainRoot = useMemo(() => {
    const m = new Map<number, ArticleRow>();
    for (const a of articles ?? []) {
      if (!m.has(a.chain_root_id) || a.is_chain_root) m.set(a.chain_root_id, a);
    }
    return m;
  }, [articles]);

  const chainRootOptions = useMemo(
    () =>
      [...nameByChainRoot.entries()]
        .map(([id, name]) => ({ value: id, label: `${name} (#${id})` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [nameByChainRoot],
  );

  const rootName = useCallback(
    (id: number) => nameByChainRoot.get(id) ?? `#${id}`,
    [nameByChainRoot],
  );

  const openCreate = () => {
    setEditing(null);
    setArticleChainRoot(null);
    setDraftIngredients([{ ...EMPTY_DRAFT }]);
    setModalOpen(true);
  };

  const openEdit = (r: RecipeRow) => {
    setEditing(r);
    setArticleChainRoot(r.article_chain_root_id);
    setDraftIngredients(
      r.ingredients.length
        ? r.ingredients.map((i) => ({
            consumable_chain_root_id: i.consumable_chain_root_id,
            amount: i.amount,
            unit_type: i.unit_type,
          }))
        : [{ ...EMPTY_DRAFT }],
    );
    setModalOpen(true);
  };

  const setIngredient = (idx: number, patch: Partial<DraftIngredient>) =>
    setDraftIngredients((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );

  const addIngredient = () =>
    setDraftIngredients((rows) => [...rows, { ...EMPTY_DRAFT }]);

  const removeIngredient = (idx: number) =>
    setDraftIngredients((rows) => rows.filter((_, i) => i !== idx));

  // Collect + validate the draft into the wire shape (client-side guard before
  // the server's stricter validation: dimension match, no nesting, …).
  const collectIngredients = (): RecipeIngredientIn[] | null => {
    const out: RecipeIngredientIn[] = [];
    const seen = new Set<number>();
    for (const r of draftIngredients) {
      if (r.consumable_chain_root_id == null || r.amount == null) {
        message.warning("Every ingredient needs a consumable and an amount.");
        return null;
      }
      if (r.amount <= 0) {
        message.warning("Ingredient amounts must be greater than zero.");
        return null;
      }
      if (seen.has(r.consumable_chain_root_id)) {
        message.warning("Each consumable may appear only once per recipe.");
        return null;
      }
      seen.add(r.consumable_chain_root_id);
      out.push({
        consumable_chain_root_id: r.consumable_chain_root_id,
        amount: r.amount,
        unit_type: r.unit_type,
      });
    }
    if (out.length === 0) {
      message.warning("A recipe needs at least one ingredient.");
      return null;
    }
    return out;
  };

  const save = useCallback(async () => {
    const ingredients = collectIngredients();
    if (ingredients === null) return;
    setSaving(true);
    try {
      if (editing) {
        await api.replaceRecipeIngredients(editing.id, ingredients);
        message.success("Recipe updated");
      } else {
        if (articleChainRoot == null) {
          message.warning("Pick the article this recipe sells as.");
          setSaving(false);
          return;
        }
        const payload: CreateRecipeRequest = {
          article_chain_root_id: articleChainRoot,
          ingredients,
        };
        await api.createRecipe(payload);
        message.success("Recipe created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Save failed");
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, articleChainRoot, draftIngredients, load, message]);

  const remove = useCallback(
    async (r: RecipeRow) => {
      try {
        await api.deleteRecipe(r.id);
        message.success("Recipe deleted");
        await load();
      } catch (e) {
        message.error(e instanceof ApiError ? e.detail : "Delete failed");
      }
    },
    [load, message],
  );

  const columns: ColumnsType<RecipeRow> = useMemo(
    () => [
      {
        title: "Recipe (sells as)",
        dataIndex: "article_chain_root_id",
        render: (id: number) => (
          <Text strong>{rootName(id)}</Text>
        ),
      },
      {
        title: "Ingredients",
        dataIndex: "ingredients",
        render: (_: unknown, r: RecipeRow) => (
          <Space direction="vertical" size={2}>
            {r.ingredients.map((i) => (
              <Text key={i.id} style={{ fontSize: 12 }}>
                {rootName(i.consumable_chain_root_id)} ·{" "}
                <b>
                  {i.amount} {UNIT_LABEL[i.unit_type]}
                </b>
              </Text>
            ))}
          </Space>
        ),
      },
      {
        title: "Available",
        dataIndex: "available",
        align: "right",
        render: (v: number | null) =>
          v == null ? (
            <Text type="secondary">—</Text>
          ) : (
            <Tag color={v <= 0 ? "red" : v < 10 ? "orange" : "green"}>
              {v} servings
            </Tag>
          ),
      },
      {
        title: "COGS / serving",
        dataIndex: "cogs_cents",
        align: "right",
        render: (v: number | null) =>
          v == null ? (
            <Text type="secondary">—</Text>
          ) : (
            <Text>{formatCents(v)}</Text>
          ),
      },
      {
        title: "",
        key: "actions",
        align: "right",
        render: (_: unknown, r: RecipeRow) => (
          <Space>
            <Button size="small" onClick={() => openEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this recipe?"
              description="The sellable article stays; only the recipe mapping is removed."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void remove(r)}
            >
              <Button size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [rootName, remove],
  );

  if (loadError) {
    return (
      <Alert type="error" showIcon message="Unavailable" description={loadError} />
    );
  }

  // chain roots already used by a recipe (1 recipe per article) — hide them
  // from the create picker so a 409 is hard to trigger.
  const usedRoots = new Set((recipes ?? []).map((r) => r.article_chain_root_id));
  const createArticleOptions = chainRootOptions.filter(
    (o) => !usedRoots.has(o.value),
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          POS · Recipes
        </Typography.Title>
        <Button type="primary" onClick={openCreate}>
          New recipe
        </Button>
      </Space>

      <Text type="secondary">
        A recipe sells as one article but depletes its ingredients. Availability
        and COGS are derived live from ingredient stock + buy prices.
      </Text>

      <Table<RecipeRow>
        rowKey="id"
        loading={recipes === null}
        columns={columns}
        dataSource={recipes ?? []}
        pagination={false}
        locale={{
          emptyText: <Empty description="No recipes yet — create one." />,
        }}
      />

      <Modal
        open={modalOpen}
        title={editing ? "Edit recipe ingredients" : "New recipe"}
        okText={editing ? "Save ingredients" : "Create recipe"}
        confirmLoading={saving}
        onOk={() => void save()}
        onCancel={() => setModalOpen(false)}
        width={680}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text type="secondary">Sells as (article)</Text>
            <Select<number>
              showSearch
              placeholder="Pick the sellable drink article"
              style={{ width: "100%", marginTop: 4 }}
              disabled={editing !== null}
              loading={articles === null}
              value={articleChainRoot ?? undefined}
              options={editing ? chainRootOptions : createArticleOptions}
              optionFilterProp="label"
              onChange={(v) => setArticleChainRoot(v)}
            />
            {editing && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                The article a recipe sells as is fixed; edit its ingredients
                below or delete + recreate to change it.
              </Text>
            )}
          </div>

          <Card size="small" title="Ingredients (per serving)">
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {draftIngredients.map((ing, idx) => (
                <Space
                  key={idx}
                  align="start"
                  style={{ width: "100%", display: "flex" }}
                  wrap
                >
                  <Select<number>
                    showSearch
                    placeholder="Consumable"
                    style={{ width: 240 }}
                    loading={articles === null}
                    value={ing.consumable_chain_root_id ?? undefined}
                    options={chainRootOptions}
                    optionFilterProp="label"
                    onChange={(v) =>
                      setIngredient(idx, {
                        consumable_chain_root_id: v,
                        // Default the unit to the consumable's own dimension.
                        unit_type: defaultUnitType(
                          articleByChainRoot.get(v)?.unit_type,
                          articleByChainRoot.get(v)?.unit_type_override,
                        ),
                      })
                    }
                  />
                  <InputNumber
                    placeholder="Amount"
                    style={{ width: 110 }}
                    min={1}
                    value={ing.amount ?? undefined}
                    onChange={(v) =>
                      setIngredient(idx, {
                        amount: v == null ? null : Number(v),
                      })
                    }
                  />
                  <Select<UnitType>
                    style={{ width: 130 }}
                    value={ing.unit_type}
                    options={UNIT_OPTIONS}
                    onChange={(v) => setIngredient(idx, { unit_type: v })}
                  />
                  <Button
                    danger
                    disabled={draftIngredients.length <= 1}
                    onClick={() => removeIngredient(idx)}
                  >
                    −
                  </Button>
                </Space>
              ))}
              <Button onClick={addIngredient}>+ Add ingredient</Button>
            </Space>
          </Card>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Amounts are in base units: mL for volume, g for weight, whole units
            for count. The server validates each ingredient against its
            consumable's dimension on save.
          </Text>
        </Space>
      </Modal>
    </Space>
  );
};

export default PosRecipesPage;
